import { useEffect, useState } from "react";
import { TIPI, type Tipo } from "@app/schema";
import type { DataServices } from "./DataProvider";

/**
 * Valori di riferimento e catalogo stampanti: elenchi piccoli, letti una volta
 * per sessione e tenuti in memoria. I gestori li aggiornano da SharePoint;
 * per vedere le modifiche basta riaprire l'app.
 */

export interface CategoriaRef {
  nome: string;
  tipo: Tipo;
}

export interface StampanteRef {
  id: number;
  nome: string;
  modello?: string;
  sede?: string;
  servizio?: string;
}

export interface ArticoloRef {
  id: number;
  nome: string;
  codice?: string;
  descrizione?: string;
  unita?: string;
  /** Prezzo scontato, IVA esclusa. */
  prezzoNetto?: number;
  iva?: number;
  stampanti: number[];
}

export interface Catalogo {
  servizi: string[];
  categorie: CategoriaRef[];
  stampanti: StampanteRef[];
  articoli: ArticoloRef[];
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** Prezzo di listino meno lo sconto percentuale (0–100). */
export function prezzoNetto(prezzo?: number, sconto?: number): number | undefined {
  if (prezzo == null) return undefined;
  return round2(prezzo * (1 - (sconto ?? 0) / 100));
}

const byOrdinePoiNome = <T extends { ordine?: number; nome: string }>(a: T, b: T) =>
  (a.ordine ?? 999) - (b.ordine ?? 999) || a.nome.localeCompare(b.nome, "it");

async function caricaCatalogo(s: DataServices): Promise<Catalogo> {
  const [servizi, categorie, stampanti, articoli] = await Promise.all([
    s.servizi.queryAll({ top: 200 }),
    s.categorie.queryAll({ top: 500 }),
    s.stampanti.queryAll({ top: 500 }),
    s.articoli.queryAll({ top: 500 }),
  ]);
  return {
    servizi: servizi
      .filter((i) => i.fields.Attivo !== false && i.fields.Title)
      .map((i) => ({ nome: i.fields.Title!, ordine: i.fields.Ordine }))
      .sort(byOrdinePoiNome)
      .map((x) => x.nome),
    categorie: categorie
      .filter((i) => i.fields.Attiva !== false && i.fields.Title && i.fields.Tipo)
      .map((i) => ({ nome: i.fields.Title!, tipo: i.fields.Tipo!, ordine: i.fields.Ordine }))
      .sort((a, b) => TIPI.indexOf(a.tipo) - TIPI.indexOf(b.tipo) || byOrdinePoiNome(a, b))
      .map(({ nome, tipo }) => ({ nome, tipo })),
    stampanti: stampanti
      .filter((i) => i.fields.Attiva !== false && i.fields.Title)
      .map((i) => ({
        id: i.id,
        nome: i.fields.Title!.trim(),
        modello: i.fields.Modello,
        sede: i.fields.Sede,
        servizio: i.fields.Servizio,
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "it")),
    articoli: articoli
      .filter((i) => i.fields.Attivo !== false && i.fields.Title)
      .map((i) => ({
        id: i.id,
        nome: i.fields.Title!.trim(),
        codice: i.fields.Codice,
        descrizione: i.fields.Descrizione,
        unita: i.fields.Unita,
        prezzoNetto: prezzoNetto(i.fields.Prezzo, i.fields.Sconto),
        iva: i.fields.Iva,
        stampanti: i.fields.Stampanti ?? [],
      }))
      .sort((a, b) => a.nome.localeCompare(b.nome, "it")),
  };
}

const cache = new WeakMap<DataServices, Promise<Catalogo>>();

export function loadCatalogo(s: DataServices): Promise<Catalogo> {
  let p = cache.get(s);
  if (!p) {
    p = caricaCatalogo(s);
    // in caso di errore si riprova alla chiamata successiva
    p.catch(() => cache.delete(s));
    cache.set(s, p);
  }
  return p;
}

export function useCatalogo(s: DataServices) {
  const [catalogo, setCatalogo] = useState<Catalogo>();
  const [error, setError] = useState<unknown>();
  useEffect(() => {
    loadCatalogo(s).then(setCatalogo, setError);
  }, [s]);
  return { catalogo, error };
}

/** Categorie selezionabili per un tipo: Generica le mostra tutte, Stampanti nessuna. */
export function categoriePerTipo(c: Catalogo, tipo: Tipo): string[] {
  if (tipo === "Stampanti") return [];
  const list = tipo === "Generica" ? c.categorie : c.categorie.filter((x) => x.tipo === tipo);
  return [...new Set(list.map((x) => x.nome))];
}

export const articoliPerStampante = (c: Catalogo, stampanteId: number) =>
  c.articoli.filter((a) => a.stampanti.includes(stampanteId));
